import { createContext, ReactNode, useEffect, useState } from 'react';
import Cookie from 'js-cookie';
import { isBefore } from 'date-fns';
import { api } from '@/utils/api';
import { useGetCurrentUser } from '@/hooks/queries/useGetCurrentUser';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '@/lang/i18n';
import { APIENDPOINT_AUTH, APIENDPOINT_LOGOUT } from '@/constants/api_endpoints';
import { COMPANY_REGISTRATION_STATUS } from '@/constants/user';

export interface AuthContextProps {
  token?: string;
  isAuthenticated: boolean | null;
}

type AuthCookie = {
  access_token?: string;
  expires?: string;
};

export const AUTH_TOKEN = 'tuempresaenestonia_auth';

export const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

export function logoutUser() {
  delete api.defaults.headers.authorization;
  Cookie.remove(AUTH_TOKEN);
  window.location.href = `${import.meta.env.VITE_API_URL}${APIENDPOINT_AUTH}${APIENDPOINT_LOGOUT}`;
}

export function AuthContextProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [token, setToken] = useState<string>();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null); // null = loading, false = not authenticated, true = authenticated

  const currentUserQuery = useGetCurrentUser();

  useEffect(() => {
    const authCookie = Cookie.get(AUTH_TOKEN);

    if (!authCookie) {
      setIsAuthenticated(false);
      return;
    }

    try {
      const currentAuth: AuthCookie = JSON.parse(authCookie);

      if (
        currentAuth.expires &&
        currentAuth.access_token &&
        isBefore(new Date(), new Date(currentAuth.expires))
      ) {
        api.defaults.headers.authorization = currentAuth.access_token;
        setToken(currentAuth.access_token);
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error(error);
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    if (currentUserQuery.isSuccess && currentUserQuery.data.user) {
      // Redirects user to registration app if their company status is still in a payment or registration step
      if (COMPANY_REGISTRATION_STATUS.includes(currentUserQuery.data.user.company.status)) {
        window.location.href =
          import.meta.env.VITE_REGISTRATION_ACTIVATION_APP_URL ||
          'https://registration.companio.co/activation';
      }

      setIsAuthenticated(true);

      if (window.$crisp) {
        const { user } = currentUserQuery.data;
        window.$crisp.push(['set', 'user:email', [user.email]]);
        window.$crisp.push(['set', 'user:nickname', [`${user.first_name} ${user.last_name}`]]);
        window.$crisp.push(['set', 'session:data', [[['ee_company', user.company.name]]]]);
      }
    } else if (currentUserQuery.isError) {
      logoutUser();
    }
  }, [currentUserQuery.data, currentUserQuery.status]);

  // Change app language to user's profile locale
  useEffect(() => {
    if (!currentUserQuery.data?.user.locale) return;

    if (SUPPORTED_LANGUAGES.includes(currentUserQuery.data?.user.locale)) {
      i18n.changeLanguage(currentUserQuery.data.user.locale);
    }
  }, [currentUserQuery.data]);

  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthenticated
      }}>
      {children}
    </AuthContext.Provider>
  );
}
